/**
 * Tiny timezone helpers built on Intl (no date library). Used by the
 * auto-post campaign scheduler (content worker + campaigns orchestrator) to
 * answer "what local date/weekday/time is it in this schedule's timezone"
 * and "when is the next slot for these weekdays at HH:MM local".
 *
 * DST edge minutes are not handled precisely (a slot inside the skipped or
 * repeated hour can land an hour off once a year) — acceptable for social
 * posts.
 */

export type LocalParts = {
  /** YYYY-MM-DD in the zone. */
  date: string
  year: number
  month: number // 1-12
  day: number // 1-31
  weekday: number // 0 = Sunday … 6 = Saturday
  hour: number // 0-23
  minute: number // 0-59
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

export function localParts(at: Date, tz: string): LocalParts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const map: Record<string, string> = {}
  for (const p of fmt.formatToParts(at)) map[p.type] = p.value
  const year = Number(map.year)
  const month = Number(map.month)
  const day = Number(map.day)
  // Some engines render midnight as "24" with hour12:false.
  const hour = Number(map.hour) % 24
  const minute = Number(map.minute)
  return {
    date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    year, month, day,
    weekday: WEEKDAYS[map.weekday ?? ''] ?? 0,
    hour, minute,
  }
}

/** UTC instant for a wall-clock time in `tz` (single-pass offset correction). */
export function zonedToUtc(year: number, month: number, day: number, hour: number, minute: number, tz: string): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute, 0, 0)
  const seen = localParts(new Date(guess), tz)
  const seenAsUtc = Date.UTC(seen.year, seen.month - 1, seen.day, seen.hour, seen.minute, 0, 0)
  return new Date(guess - (seenAsUtc - guess))
}

/**
 * Next slot strictly after `from` for the given weekdays at HH:MM local.
 * Returns null when `days` is empty. Scans 8 days so a single-weekday
 * schedule still resolves.
 */
export function nextSlot(
  from: Date,
  days: readonly number[],
  hour: number,
  minute: number,
  tz: string,
): { at: Date; localDate: string } | null {
  if (days.length === 0) return null
  const set = new Set(days)
  for (let offset = 0; offset <= 8; offset++) {
    const probe = new Date(from.getTime() + offset * 86_400_000)
    const p = localParts(probe, tz)
    if (!set.has(p.weekday)) continue
    const at = zonedToUtc(p.year, p.month, p.day, hour, minute, tz)
    if (at.getTime() > from.getTime()) return { at, localDate: p.date }
  }
  return null
}

export function parseDays(raw: string | null | undefined): number[] {
  if (!raw) return []
  const out = new Set<number>()
  for (const part of raw.split(',')) {
    const n = Number(part.trim())
    if (Number.isInteger(n) && n >= 0 && n <= 6) out.add(n)
  }
  return [...out].sort((a, b) => a - b)
}

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

export function describeDays(days: readonly number[]): string {
  const sorted = [...new Set(days)].sort((a, b) => a - b)
  if (sorted.length === 7) return 'Every day'
  if (sorted.join(',') === '1,2,3,4,5') return 'Weekdays'
  if (sorted.join(',') === '0,6') return 'Weekends'
  return sorted.map((d) => DAY_LABELS[d]).join('/')
}

export function formatLocalTime(hour: number, minute: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  const suffix = hour < 12 ? 'AM' : 'PM'
  return `${h12}:${String(minute).padStart(2, '0')} ${suffix}`
}
